// src/common/Navbar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/A-logomain4.png';
import '../common/solox-btn.css';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isRetreatsOpen, setIsRetreatsOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const servicesRef = useRef(null);
  const retreatsRef = useRef(null);
  const mobileServicesRef = useRef(null);
  const mobileRetreatsRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  // Helper: Close other dropdown when one is opened
  const openOnlyThisDropdown = (type) => {
    if (type === 'services') {
      setIsServicesOpen(true);
      setIsRetreatsOpen(false);
    } else if (type === 'retreats') {
      setIsRetreatsOpen(true);
      setIsServicesOpen(false);
    }
  };

  

  useEffect(() => {
    const handleDesktopClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
      if (retreatsRef.current && !retreatsRef.current.contains(event.target)) {
        setIsRetreatsOpen(false);
      }
    };

    document.addEventListener("click", handleDesktopClickOutside);
    return () => {
      document.removeEventListener("click", handleDesktopClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleMobileClickOutside = (event) => {
      if (hamburgerRef.current && hamburgerRef.current.contains(event.target)) return;

      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsServicesOpen(false);
        setIsRetreatsOpen(false);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("touchstart", handleMobileClickOutside);
    return () => {
      document.removeEventListener("touchstart", handleMobileClickOutside);
    };
  }, []);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setIsServicesOpen(false);
    setIsRetreatsOpen(false);

    if (sectionId === 'about') {
      navigate('/about');
      return;
    }
    if (sectionId === 'services') {
      navigate('/services');
      return;
    }

    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }

    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, 120);
  };

  const services = [
    { name: "Herbal Remedies", path: "/services/herbal-remedies" },
    { name: "Shirodhara Treatment", path: "/services/shirodhara" },
    { name: "Panchakarma Therapy", path: "/services/panchakarma" },
    { name: "Detox & Rejuvenation", path: "/services/detox" },
    { name: "Abhyanga Massage", path: "/services/abhyanga" },
    { name: "Holistic Healing", path: "/services/holistic-healing", icon: "" },
  ];

  const retreats = [
    { name: "Cardiology Preventive", path: "/retreats/cardiology-preventive" },
    { name: "Neurology Retreat", path: "/retreats/neurology-retreat" },
    { name: "Inflammatory & Autoimmune", path: "/retreats/inflammatory-autoimmune" },
    { name: "Lifestyle Disorders", path: "/retreats/lifestyle-disorders" },
    { name: "NCD Management", path: "/retreats/non-communicable-diseases" },
    { name: "Oncology Supportive Care", path: "/retreats/oncology-supportive-care" },
    { name: "Pain Management", path: "/retreats/pain-management" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // New helper: Check if this exact child page is active
  const isChildActive = (childPath) => {
    return location.pathname === childPath;
  };

  const activeClass = "text-[#0B2A2C] font-bold";
  const inactiveClass = "text-black hover:text-[#0B2A2C] transition-colors";

  const childActiveClass = "text-[#0B2A2C] font-bold bg-[#EBE0CF]/50"; // Highlight style for active child
  const childInactiveClass = "text-[#0B2A2C] hover:bg-[#EBE0CF] transition-all";

  return (
    <nav className="flex justify-between items-center p-4 md:px-8 bg-[#E5D8C2] font-serif sticky top-0 z-50 gap-3 shadow-md">
      {/* Logo */}
      <div 
  onClick={() => {
    if (location.pathname === '/') {
      navigate('/splash');
    } else {
      navigate('/');
    }
  }}
  className="cursor-pointer"
>
  <img src={logo} alt="Ayusanjivni Logo" className="h-20  w-auto" />
</div>

      {/* Desktop Menu */}
      <ul className="hidden lg:flex items-center list-none m-0 p-0 gap-8 text-base text-black-700">
        <li>
          <a
            href="#hero"
            onClick={(e) => handleNavClick(e, 'hero')}
            className={`${isActive("/") ? activeClass : inactiveClass}`}
          >
            Home
          </a>
        </li>
        <li>
          <a
            href="/about"
            onClick={(e) => handleNavClick(e, 'about')}
            className={`${isActive("/about") ? activeClass : inactiveClass}`}
          >
            About
          </a>
        </li>

        {/* Services Dropdown */}
        <li className="relative group" ref={servicesRef}>
          <button
            onClick={() => setIsServicesOpen(!isServicesOpen)}
            className={`flex items-center gap-2 transition-colors ${isActive("/services") ? activeClass : inactiveClass}`}
          >
            Services
            <svg
              className={`w-4 h-4 transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isServicesOpen && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 bg-white/30 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-3 z-50 animate-dropdownFade">
              <div className="px-4 py-3 border-b border-white/30">
                <p className="text-xs text-[#0B2A2C] uppercase tracking-wider font-semibold">Our Healing Therapies</p>
              </div>
              <div className="flex flex-col py-2">
                {services.map((service) => (
                  <a
                    key={service.path}
                    href={service.path}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(service.path);
                      setIsServicesOpen(false);
                    }}
                    className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isChildActive(service.path) ? childActiveClass : childInactiveClass
                    }`}
                  >
                    <span className="text-2xl">{service.icon}</span>
                    {service.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </li>

        {/* Retreats Dropdown */}
        <li className="relative group" ref={retreatsRef}>
          <button
            onClick={() => setIsRetreatsOpen(!isRetreatsOpen)}
            className={`flex items-center gap-2 transition-colors ${isActive("/retreats") ? activeClass : inactiveClass}`}
          >
            Retreats
            <svg
              className={`w-4 h-4 transition-transform ${isRetreatsOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isRetreatsOpen && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-80 bg-white/30 backdrop-blur-xl rounded-3xl shadow-2xl border border-[#0B2A2C]/20 p-4 z-50">
              <div className="px-4 py-3 border-b border-[#0B2A2C]/20 mb-3">
                <p className="text-xs text-[#0B2A2C] uppercase font-bold">Specialized Clinical Retreats</p>
              </div>
              <div className="grid grid-cols-1 gap-1">
                {retreats.map((retreat) => (
                  <a
                    key={retreat.path}
                    href={retreat.path}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(retreat.path);
                      setIsRetreatsOpen(false);
                    }}
                    className={`px-5 py-3 rounded-xl transition-all duration-300 text-left ${
                      isChildActive(retreat.path) ? childActiveClass : childInactiveClass
                    }`}
                  >
                    {retreat.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </li>

        <li>
          <a
            href="#programs"
            onClick={(e) => handleNavClick(e, 'programs')}
            className={`${isActive("/programs") ? activeClass : inactiveClass} transition-colors`}
          >
            Programs
          </a>
        </li>
        <li>
          <a
            href="#nutrition"
            onClick={(e) => handleNavClick(e, 'nutrition')}
            className={`${isActive("/nutrition") ? activeClass : inactiveClass} transition-colors`}
          >
            Nutrition
          </a>
        </li>



<li>
  <button
    onClick={() => navigate('/facilities')}
    className={`${isActive("/facilities") ? activeClass : inactiveClass}`}
  >
    Facilities
  </button>
</li>













        <li>









          <button
            onClick={() => navigate('/contact')}
            className={`${isActive("/contact") ? activeClass : inactiveClass}`}
          >
            Contact
          </button>
        </li>
      </ul>

      <button
        onClick={() => navigate('/BookNow')}
        className="solox-btn hidden lg:block  bg-[#0B2A2C] text-white border-2 border-[#0B2A2C] py-2 px-6 rounded-full font-medium hover:bg-[#1A4D4F] hover:text-[#0B2A2C] transition"
      >
        <span>Book Now</span>
      </button>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMenuOpen(false)}
          />

          <div
            className="fixed top-0 right-0 h-full z-50 bg-[#EBE0CF] flex flex-col w-full sm:w-4/5 md:w-3/5 lg:hidden shadow-2xl"
            ref={mobileMenuRef}
          >
            <div className="flex justify-between items-center p-6 border-b">
              <img src={logo} className="h-14" alt="Logo" />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-4xl text-[#0B2A2C]"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-8">
              <ul className="flex flex-col gap-6 text-lg">
                <li>
                  <a
                    href="#hero"
                    onClick={(e) => handleNavClick(e, 'hero')}
                    className={`${isActive("/") ? activeClass : inactiveClass}`}
                  >
                    Home
                  </a>
                </li>
                <li>
                  <a
                    href="/about"
                    onClick={(e) => { handleNavClick(e, 'about'); setIsMenuOpen(false); }}
                    className={`${isActive("/about") ? activeClass : inactiveClass} block py-2`}
                  >
                    About
                  </a>
                </li>

                {/* Services Dropdown - Mobile */}
                <li ref={mobileServicesRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isServicesOpen) {
                        setIsServicesOpen(false);
                      } else {
                        openOnlyThisDropdown('services');
                      }
                    }}
                    className={`flex items-center justify-between w-full transition-colors ${
                      isActive("/services") ? activeClass : inactiveClass
                    }`}
                  >
                    Services
                    <svg
                      className={`w-4 h-4 transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isServicesOpen && (
                    <div className="pl-6 bg-white/70 rounded-xl mt-3">
                      {services.map((service) => (
                        <a
                          key={service.path}
                          href={service.path}
                          onClick={(e) => {
                            e.preventDefault();
                            setIsServicesOpen(false);
                            setIsMenuOpen(false);
                            setTimeout(() => navigate(service.path), 50);
                          }}
                          className={`block py-3 px-4 rounded-lg transition-all ${
                            isChildActive(service.path) ? childActiveClass : childInactiveClass
                          }`}
                        >
                          {service.name}
                        </a>
                      ))}
                    </div>
                  )}
                </li>

                {/* Retreats Dropdown - Mobile */}
                <li ref={mobileRetreatsRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isRetreatsOpen) {
                        setIsRetreatsOpen(false);
                      } else {
                        openOnlyThisDropdown('retreats');
                      }
                    }}
                    className={`flex items-center justify-between w-full transition-colors ${
                      isActive("/retreats") ? activeClass : inactiveClass
                    }`}
                  >
                    Retreats
                    <svg
                      className={`w-4 h-4 transition-transform ${isRetreatsOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isRetreatsOpen && (
                    <div className="pl-6 bg-white/70 rounded-xl mt-3">
                      {retreats.map((retreat) => (
                        <a
                          key={retreat.path}
                          href={retreat.path}
                          onClick={(e) => {
                            e.preventDefault();
                            setIsRetreatsOpen(false);
                            setIsMenuOpen(false);
                            setTimeout(() => navigate(retreat.path), 50);
                          }}
                          className={`block py-3 px-4 rounded-lg transition-all ${
                            isChildActive(retreat.path) ? childActiveClass : childInactiveClass
                          }`}
                        >
                          {retreat.name}
                        </a>
                      ))}
                    </div>
                  )}
                </li>

                <li>
                  <a
                    href="#programs"
                    onClick={(e) => { handleNavClick(e, 'programs'); setIsMenuOpen(false); }}
                    className={`${isActive("/programs") ? activeClass : inactiveClass} block py-2`}
                  >
                    Programs
                  </a>
                </li>
                <li>
                  <a
                    href="#nutrition"
                    onClick={(e) => { handleNavClick(e, 'nutrition'); setIsMenuOpen(false); }}
                    className={`${isActive("/nutrition") ? activeClass : inactiveClass} block py-2`}
                  >
                    Nutrition
                  </a>
                </li>

                <li>
  <button
    onClick={() => {
      navigate('/facilities');
      setIsMenuOpen(false);
    }}
    className={`${isActive("/facilities") ? activeClass : inactiveClass}`}
  >
    Facilities
  </button>
</li>
                <li>
                  <button
                    onClick={() => { navigate('/contact'); setIsMenuOpen(false); }}
                    className={`${isActive("/contact") ? activeClass : inactiveClass}`}
                  >
                    Contact
                  </button>
                </li>
              </ul>

              <div className="mt-10 mb-8">
                <button
                  onClick={() => { navigate('/BookNow'); setIsMenuOpen(false); }}
                  className="solox-btn w-full bg-[#0B2A2C] text-white border-2 border-[#0B2A2C] py-5 rounded-full font-medium text-xl hover:bg-[#1A4D4F] transition shadow-lg"
                >
                  <span>Book Now</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <button
        ref={hamburgerRef}
        className="lg:hidden text-4xl text-[#0B2A2C] z-50 relative"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle menu"
      >
        {isMenuOpen ? '' : '☰'}
      </button>
      
    </nav>
  );
};

export default Navbar;